/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect} from "react";
import {Reservation} from "types/entities/Reservation";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import CompanyFormCheckbox from "pages/Front/Booking/components/CompanyForm/CompanyFormCheckbox";
import {DEFAULT_HALL, DEFAULT_HALL_ID, EMAIL_VERIFICATION_REQUIRED, MIN_COUNT_FOR_INQUIRY} from "helpers/config";
import {HallCode} from "types/enums/HallCode";
import {useTypedSelector} from "helpers/reducers";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {consoleLog} from "utils/utility";
import ButtonSubmit from "pages/Front/Booking/components/ButtonSubmit";
import CardLabel from "pages/Front/Booking/components/BookingCustomer/CardLabel";
import {CustomerType} from "pages/Front/Booking/components/BookingCustomer/BookingCustomerForm";
import BookedExtraItems from "pages/Front/Booking/components/BookingCustomer/BookedExtraItems";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {PackageItemItemType} from "types/entities/PackageItem";
import {PackageCategory} from "types/enums/PackageCategory";
import BookingOverview from "pages/Front/Booking/components/BookingCustomer/BookingOverview";
import {ThunkAction} from "model/store/thunk";
import BookingAction from "store/booking/BookingAction";
import {useDispatch} from "useDispatch";
import {toast} from "react-toastify";

interface BookingLoginReservationDetailSummaryFormProps {
    reservation: Reservation;
}

const BookingLoginReservationDetailSummaryForm = (props: BookingLoginReservationDetailSummaryFormProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const hall = useTypedSelector(state => state.hallState.hall);
    const emailVerified = EMAIL_VERIFICATION_REQUIRED ? useTypedSelector(state => state.bookingState.bookingProcess.emailVerified) : true;
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const packagesCart = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const selectedPackage = useTypedSelector(state => state.bookingState.bookingProcess.package);
    const selectedCategory: PackageCategory = (selectedPackage?.category as PackageCategory) || bookingFilter.category;

    // TODO: neviem ako zistit ci je company alebo private, doplnit
    const selectedCustomerType = CustomerType.Private;

    const packagesCartItemsOutstanding =
        packagesCart?.packagesCartItems &&
        packagesCart?.packagesCartItems.filter((packagesCartItem: PackagesCartItem) => {
            if (packagesCartItem.packageItem?.type === PackageItemItemType.BasePrice) {
                return false;
            }
            if (packagesCartItem.packageItem?.activity_id === null) {
                return true;
            }
        });

    interface AdditionalUpsellFormInputs {
        home_hall_id: number;
        tos: boolean;
    }

    const AdditionalUpsellSchema = yup.object().shape({
        home_hall_id: yup.number().required(),
        tos: yup.bool().oneOf([true], "Field must be checked"),
    });

    const defaultValues = {
        home_hall_id: DEFAULT_HALL_ID,
        tos: false,
    };

    const {
        register,
        handleSubmit,
        setValue,
        formState: {errors},
    } = useForm({
        resolver: yupResolver(AdditionalUpsellSchema),
        defaultValues: defaultValues,
    });

    const onSubmitError = data => {
        consoleLog(data);
    };

    const onSubmitHandler = (data: AdditionalUpsellFormInputs) => {
        if (packagesCart) {
            dispatch<ThunkAction<any, any>>(BookingAction.fetchPayUpsellsCart(packagesCart)).then(r => {});
        } else {
            toast.error("Error on booking, contact support");
            return;
        }
    };

    useEffect(() => {
        console.log("useEffect - props.reservation", props.reservation);
    }, [props.reservation]);

    return (
        <form
            className={"BookingLoginReservationDetailSummaryForm w-full grid grid-cols-1 md:grid-cols-2 gap-6"}
            onSubmit={handleSubmit(onSubmitHandler, onSubmitError)}
        >
            <input {...register("home_hall_id")} type={"hidden"} value={DEFAULT_HALL_ID} />

            <div className={"col-span-1 space-y-6"}>
                <BookingOverview />
            </div>

            <div className={"col-span-1 space-y-6"}>
                {packagesCartItemsOutstanding && packagesCartItemsOutstanding?.length > 0 && (
                    <BookedExtraItems packagesCartItemsOutstanding={packagesCartItemsOutstanding} />
                )}
                <CardComponent className={"!p-0"}>
                    <CompanyFormCheckbox disabled={!emailVerified} errors={errors} name={"tos"} register={register} required={true}>
                        {t("I accept the no refund policy, general terms and conditions and privacy policy of the booking")}
                        <a
                            className={"LinkToTos font-bold text-xs w-auto inline-flex"}
                            href={hall ? hall.condition_terms_link : "/terms"}
                            rel={"noreferrer"}
                            target={"_blank"}
                            title={t("Terms and conditions")}
                        >
                            ({t("Read terms & conditions")})
                        </a>
                    </CompanyFormCheckbox>
                </CardComponent>

                {/*<CardComponent className={"text-sm"}>*/}
                {/*    <span>*/}
                {/*        {t(*/}
                {/*            "ArboPark may process and use the data collected during the conclusion of the contract to fulfill the obligations arising from the purchase contract and for marketing purposes."*/}
                {/*        )}*/}
                {/*        {t("This consent can be revoked at any time in writing or by email")}*/}
                {/*    </span>*/}
                {/*</CardComponent>*/}

                {DEFAULT_HALL === HallCode.ArboPark && (
                    <CardComponent>
                        {selectedCustomerType === CustomerType.Private || bookingFilter.children + bookingFilter.adults < MIN_COUNT_FOR_INQUIRY ? (
                            <div className={"flex flex-col space-y-1"}>
                                <CardLabel title={t("Your payment will be processed on the site of our payment provider")} />
                                <hr />
                                <div className={"flex flex-col pt-1 pb-3"}>
                                    <div className={"flex flex-col max-w-xs m-auto w-full"}>
                                        <ButtonSubmit
                                            buttonType={"cta"}
                                            className={"rounded-md"}
                                            disabled={!emailVerified}
                                            icon={"card"}
                                            id={"ButtonPayNow"}
                                            label={t("Pay now")}
                                            onClick={handleSubmit(onSubmitHandler, onSubmitError)}
                                            // onClick={() =>
                                            //     submitMegaHandler(
                                            //         () => verifyValueHandler(verificationType),
                                            //         handleSubmit(submitCompany, submitCompanyError),
                                            //         payNowHandler
                                            //     )
                                            // }
                                        />
                                    </div>
                                    <img alt={"Worldline Logo"} className={"h-4 mt-4"} src={"/img/logo-worldline.svg"} />
                                </div>
                            </div>
                        ) : (
                            <div className={"flex flex-col space-y-1"}>
                                <CardLabel title={t("Your inquiry will be processed as soon as possible")} />
                                <hr />
                                <div className={"flex flex-col pt-1 pb-4"}>
                                    <div className={"flex flex-col max-w-xs m-auto w-full"}>
                                        <ButtonSubmit
                                            buttonType={"cta"}
                                            className={"rounded-md"}
                                            disabled={!emailVerified}
                                            icon={"info"}
                                            id={"ButtonInquireNow"}
                                            label={t("Send inquiry")}
                                            onClick={handleSubmit(onSubmitHandler, onSubmitError)}
                                            // onClick={() =>
                                            //     submitMegaHandler(
                                            //         () => verifyValueHandler(verificationType),
                                            //         handleSubmit(submitCompany, submitCompanyError),
                                            //         inquireNowHandler
                                            //     )
                                            // }
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardComponent>
                )}
            </div>
        </form>
    );
};

export default BookingLoginReservationDetailSummaryForm;
